// services/ReportService.js - Service for handling promotion reports
import { collection, addDoc, getDocs, query, orderBy, where, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

class ReportService {
  constructor() {
    this.collectionName = 'promotion_reports';
  }

  /**
   * Submit a new report for a promotion
   */
  async submitReport(reportData) {
    try {
      const report = {
        ...reportData,
        status: 'pending', // pending, reviewed, resolved
        rewarded: false, // track if user has been rewarded
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, this.collectionName), report);
      
      return {
        success: true,
        reportId: docRef.id,
        message: 'Report submitted successfully'
      };
    } catch (error) {
      console.error('Error submitting report:', error);
      return {
        success: false,
        error: error.message || 'Failed to submit report'
      };
    }
  }

  /**
   * Get all reports (for admin dashboard)
   */
  async getAllReports() {
    try {
      const q = query(
        collection(db, this.collectionName),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const reports = [];
      
      querySnapshot.forEach((doc) => {
        reports.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return {
        success: true,
        data: reports
      };
    } catch (error) {
      console.error('Error fetching reports:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch reports',
        data: []
      };
    }
  }

  /**
   * Get reports by status
   */
  async getReportsByStatus(status) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const reports = [];
      
      querySnapshot.forEach((doc) => {
        reports.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return {
        success: true,
        data: reports
      };
    } catch (error) {
      console.error('Error fetching reports by status:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch reports',
        data: []
      };
    }
  }

  /**
   * Update report status (for admin)
   */
  async updateReportStatus(reportId, status, adminNotes = '') {
    try {
      const reportRef = doc(db, this.collectionName, reportId);
      
      const updateData = {
        status,
        adminNotes,
        updatedAt: new Date()
      };

      // If marking as reviewed and not already rewarded, mark for reward
      if (status === 'reviewed') {
        updateData.reviewedAt = new Date();
      }

      await updateDoc(reportRef, updateData);

      return {
        success: true,
        message: 'Report status updated successfully'
      };
    } catch (error) {
      console.error('Error updating report status:', error);
      return {
        success: false,
        error: error.message || 'Failed to update report status'
      };
    }
  }

  /**
   * Mark report as rewarded
   */
  async markReportAsRewarded(reportId) {
    try {
      const reportRef = doc(db, this.collectionName, reportId);
      
      await updateDoc(reportRef, {
        rewarded: true,
        rewardedAt: new Date(),
        updatedAt: new Date()
      });

      return {
        success: true,
        message: 'Report marked as rewarded'
      };
    } catch (error) {
      console.error('Error marking report as rewarded:', error);
      return {
        success: false,
        error: error.message || 'Failed to mark report as rewarded'
      };
    }
  }

  /**
   * Delete a report (for admin)
   */
  async deleteReport(reportId) {
    try {
      const reportRef = doc(db, this.collectionName, reportId);
      await deleteDoc(reportRef);

      return {
        success: true,
        message: 'Report deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting report:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete report'
      };
    }
  }

  /**
   * Get reports for a specific promotion
   */
  async getReportsForPromotion(promotionId) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('promotionId', '==', promotionId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const reports = [];

      querySnapshot.forEach((doc) => {
        reports.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return {
        success: true,
        data: reports
      };
    } catch (error) {
      console.error('Error fetching promotion reports:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch promotion reports',
        data: []
      };
    }
  }
}

// Create and export a singleton instance
const reportService = new ReportService();
export default reportService;